import MojitoCore from 'mojito/core';
import { createSlice } from '@reduxjs/toolkit';

const NamedStorageService = MojitoCore.Services.Storage.NamedService;
const reduxInstance = MojitoCore.Services.redux;

const STORAGE_NAMES = {
    LIVE: 'topSportLive',
    FEATURED: 'topSportFeatured',
    LIVE_AND_FEATURED: 'topSportLiveAndFeatured',
};

const topSportLiveStorage = new NamedStorageService(STORAGE_NAMES.LIVE);
const topSportFeaturedStorage = new NamedStorageService(STORAGE_NAMES.FEATURED);
const topSportLiveAndFeaturedStorage = new NamedStorageService(STORAGE_NAMES.LIVE_AND_FEATURED);

/**
 * Defines the state of the top sports store.
 *
 * @typedef TopSportsState
 *
 * @property {string} topSportLive - Selected sport for in-play events.
 * @property {string} topSportFeatured - Selected sport for featured events.
 * @property {string} topSportLiveAndFeatured - Selected sport for live-and-future events.
 *
 * @memberof Mojito.Modules.TopSports
 */

/**
 * The name of the top sports state property. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Modules.TopSports
 */
export const STORE_KEY = 'modules/top-sports';

export const INITIAL_STATE = {
    topSportLive: topSportLiveStorage.getItem(),
    topSportFeatured: topSportFeaturedStorage.getItem(),
    topSportLiveAndFeatured: topSportLiveAndFeaturedStorage.getItem(),
};

export const { reducer, actions } = createSlice({
    name: 'topSports',
    initialState: INITIAL_STATE,
    reducers: {
        setTopSportLive(state, { payload }) {
            state.topSportLive = payload;
        },
        setTopSportFeatured(state, { payload }) {
            state.topSportFeatured = payload;
        },
        setTopSportLiveAndFeatured(state, { payload }) {
            state.topSportLiveAndFeatured = payload;
        },
        reset() {
            return { ...INITIAL_STATE };
        },
    },
});

/**
 * Top Sports related actions.
 *
 * @class TopSportsActions
 * @name actions
 * @memberof Mojito.Modules.TopSports
 */

/**
 * Set selected sport for in-play events.
 *
 * @function setTopSportLive
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {string} payload - Selected sport for in-play events.
 * @memberof Mojito.Modules.TopSports.actions
 */

/**
 * Set selected sport for featured events.
 *
 * @function setTopSportFeatured
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {string} payload - Selected sport for featured events.
 * @memberof Mojito.Modules.TopSports.actions
 */

/**
 * Set selected sport for live-and-future events.
 *
 * @function setTopSportLiveAndFeatured
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {string} payload - Selected sport for live-and-future events.
 * @memberof Mojito.Modules.TopSports.actions
 */

reduxInstance.actionListener.startListening({
    actionCreator: actions.setTopSportLive,
    effect: (action, listenerApi) => {
        const state = listenerApi.getState()[STORE_KEY].topSportLive;
        topSportLiveStorage.setItem(state);
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: actions.setTopSportFeatured,
    effect: (action, listenerApi) => {
        const state = listenerApi.getState()[STORE_KEY].topSportFeatured;
        topSportFeaturedStorage.setItem(state);
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: actions.setTopSportLiveAndFeatured,
    effect: (action, listenerApi) => {
        const state = listenerApi.getState()[STORE_KEY].topSportLiveAndFeatured;
        topSportLiveAndFeaturedStorage.setItem(state);
    },
});

reduxInstance.injectReducer(STORE_KEY, reducer);
